import {
  type InboundRingMode,
  type OutboundLineInput,
  phoneSetupClient,
  type PhoneMessageInput,
  type PhoneMessagePurpose,
} from "@phone/phoneSetupClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

export const phoneSetupKeys = {
  all: ["phone-setup"] as const,
  detail: (sessionKey: string) => ["phone-setup", sessionKey] as const,
  callerId: (sessionKey: string) =>
    ["phone-setup", sessionKey, "caller-id"] as const,
  outboundLines: (sessionKey: string) =>
    ["phone-setup", sessionKey, "outbound-lines"] as const,
};

const CALLER_ID_POLL_MS = 5_000;

export const usePhoneSetup = (sessionKey: string) =>
  useQuery({
    queryKey: phoneSetupKeys.detail(sessionKey),
    queryFn: phoneSetupClient.get,
  });

export const useUpdatePhoneMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      purpose,
      input,
    }: {
      purpose: PhoneMessagePurpose;
      input: PhoneMessageInput;
    }) => phoneSetupClient.setMessage(purpose, input),
    meta: { action: "save the message" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: phoneSetupKeys.all }),
  });
};

export const useUploadPhoneMessageAudio = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      purpose,
      file,
    }: {
      purpose: PhoneMessagePurpose;
      file: File;
    }) => phoneSetupClient.uploadMessageAudio(purpose, file),
    meta: { handled: true },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: phoneSetupKeys.all }),
  });
};

export const useUpdateHandoffDestinations = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (destinations: string[]) =>
      phoneSetupClient.setHandoffDestinations(destinations),
    meta: { handled: true },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: phoneSetupKeys.all }),
  });
};

export const useUpdateOutboundConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      enabled?: boolean;
      concurrencyLimit?: number;
      disclosureSpoken?: boolean;
      inboundRing?: InboundRingMode;
      outboundComputerDefault?: boolean;
      holdCheckInSeconds?: number;
      holdMaxSeconds?: number;
    }) => phoneSetupClient.setOutboundConfig(input),
    meta: { handled: true },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: phoneSetupKeys.all }),
  });
};

export const useUpdateOutboundLines = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lines: OutboundLineInput[]) =>
      phoneSetupClient.setOutboundLines(lines),
    meta: { handled: true },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: phoneSetupKeys.all }),
  });
};

export const useStartCallerIdVerification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (numberE164: string) =>
      phoneSetupClient.startCallerIdVerification(numberE164),
    meta: { handled: true },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: phoneSetupKeys.all }),
  });
};

export const useClearCallerId = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => phoneSetupClient.clearCallerId(),
    meta: { action: "clear the caller ID" },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: phoneSetupKeys.all }),
  });
};

export const useCallerIdVerification = (
  sessionKey: string,
  pending: boolean,
) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: phoneSetupKeys.callerId(sessionKey),
    queryFn: phoneSetupClient.getCallerId,
    enabled: pending,
    refetchInterval: (q) =>
      q.state.data?.verificationPending === false ? false : CALLER_ID_POLL_MS,
  });
  // Not in the queryFn: the detail key prefixes this query's, so invalidating
  // mid-fetch would restart it forever. `exact` keeps it off this query too.
  const finished = query.data?.verificationPending === false;
  useEffect(() => {
    if (!finished) return;
    void queryClient.invalidateQueries({
      queryKey: phoneSetupKeys.detail(sessionKey),
      exact: true,
    });
  }, [finished, queryClient, sessionKey]);
  return query;
};
